import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useUsers from 'contexts/basicData/useUsers';
import {
  formatDate,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';
import CaseOpenLink from 'components/CaseOpenLink';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { AssignmentDTO, TranslationLookups } from 'api/model';
import { GoSupportWidgetsEnum, LongTitles, UPDATE_INTERVAL } from '../utils';
import {
  Centered,
  getTableRowClassName,
} from 'components/Dashboard/table-helpers';
import useTranslations from 'contexts/basicData/useTranslations';
import { useGoSupportGetStandbyTimeAssignments } from 'api/go-support/go-support';
import { makeStringComparator } from 'utils/sorting';

const TABLE_NAME = 'standby-time-assignments';

const StandbyTimeAssignmentsWidget = () => {
  const [data, setData] = useState<AssignmentDTO[]>([]);
  const users = useUsers();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();
  const translations = useTranslations();

  const getStandbyAssignments = useGoSupportGetStandbyTimeAssignments(
    {
      startDate: daterange[0] ?? undefined,
      endDate: daterange[1] ?? undefined,
      businessUnitIDs: selectedBusinessUnits,
    },
    {
      query: {
        refetchInterval: UPDATE_INTERVAL,
      },
    }
  );

  useEffect(() => {
    if (getStandbyAssignments.data) {
      setData(getStandbyAssignments.data);
    } else {
      setData([] as AssignmentDTO[]);
    }
  }, [getStandbyAssignments.data]);

  const filteredNeedStandby = useMemo(() => {
    let filtered = [...data];

    if (searchText.length > 0) {
      filtered = filtered.length
        ? filtered
            .filter((c) => {
              return (
                c.registrationNumber
                  .toLowerCase()
                  .includes(searchText.toLowerCase()) ||
                (users[c.assignedToID ?? -1]?.name ?? '')
                  .toLowerCase()
                  .includes(searchText.toLowerCase())
              );
            })
            .sort((a, b) => b.bookedTo.getTime() - a.bookedTo.getTime()) // Latest first
        : [];
    }

    return filtered;
  }, [data, searchText, users]);

  const columnSettings: ColumnSetting<AssignmentDTO, TranslationLookups>[] =
    useMemo(() => {
      const columns: ColumnSetting<AssignmentDTO, TranslationLookups>[] = [
        {
          head: 'Nr',
          render: (e, t, f, i) => i + 1,
          width: 15,
        },
        {
          head: 'Reg.nr',
          render: (row) => `${row.registrationNumber}`,
          width: 60,
          sortFunction: (a, b) => {
            return a.registrationNumber.localeCompare(b.registrationNumber);
          },
        },
        {
          head: 'Ärendetyp',
          render: (row) => (
            <span>{translations.caseTypes[row.caseType] ?? ''}</span>
          ),
          width: 100,
          sortFunction: makeStringComparator(
            (row) => translations.caseTypes[row.caseType] ?? ''
          ),
        },
        {
          head: 'Uppdragstyp',
          render: (row) => (
            <span>
              {translations.assignmentTypes[row.assignmentType ?? 0] ?? ''}
            </span>
          ),
          width: 100,
          sortFunction: makeStringComparator(
            (row) => translations.assignmentTypes[row.assignmentType ?? 0] ?? ''
          ),
        },
        {
          head: 'Status',
          render: (row) =>
            `${row.assignmentStatus ? translations.assignmentStatuses[row.assignmentStatus] : ''}`,
          width: 50,
          sortFunction: (a, b) => {
            return a.assignmentStatus - b.assignmentStatus;
          },
        },
        {
          head: 'Avbokningstyp',
          render: (row) =>
            `${row.assignmentCancelType ? translations.cancelTypes[row.assignmentCancelType] : ''}`,
          width: 80,
          sortFunction: (a, b) => {
            return (
              (a.assignmentCancelType ?? 0) - (b.assignmentCancelType ?? 0)
            );
          },
        },
        {
          head: 'Datum',
          render: (row) => `${formatDate(row.bookedTo)}`,
          width: 70,
          sortFunction: (a, b) =>
            (a.bookedTo?.getTime() ?? 0) - (b.bookedTo?.getTime() ?? 0),
        },
        {
          head: 'Fälttestare',
          render: (row) => {
            return (
              <span>
                {users[row.assignedToID ?? -1] ? users[row.assignedToID ?? -1].name : ''}
              </span>
            );
          },
          width: 100,
          sortFunction: (a, b) =>
            (users[a.assignedToID ?? -1]?.name ?? '').localeCompare(
              users[b.assignedToID ?? -1]?.name ?? ''
            ),
        },
        {
          head: 'Berättigad tid',
          render: (row) =>
            `${row.isStandbyEligible === true ? 'Ja' : row.isStandbyEligible === false ? 'Nej' : 'Inte satt'}`,
          width: 50,
          sortFunction: (a, b) => {
            return a.isStandbyEligible === b.isStandbyEligible
              ? 0
              : a.isStandbyEligible === true
                ? -1
                : 1;
          },
        },
        {
          head: 'Standby-tid',
          render: (row) =>
            `${row.standByAssignmentTime === 0 ? '0h' : row.standByAssignmentTime ? getHoursAndMinutesFromMillisecondsString(row.standByAssignmentTime) : '-'}`,
          width: 50,
          sortFunction: (a, b) => {
            return (
              a.standByAssignmentTime ?? 0 - (b.standByAssignmentTime ?? 0)
            );
          },
        },
        {
          head: ' ',
          render: (row) => (
            <Centered>
              <CaseOpenLink caseID={row.caseID} />
            </Centered>
          ),
          width: 20,
        },
      ];
      return columns;
    }, [
      translations.assignmentStatuses,
      translations.assignmentTypes,
      translations.cancelTypes,
      translations.caseTypes,
      users,
    ]);
  return (
    <Widget
      title={LongTitles[GoSupportWidgetsEnum.StandbyTimeAssignments]}
      count={data.length}
      isLoading={getStandbyAssignments.isLoading}
      info="Status kring standby-tid för uppdrag."
    >
      <Table
        columnSettings={columnSettings}
        rows={filteredNeedStandby}
        rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
        useColumnWidthAsFlexAmount
        renderProps={translations}
      />
    </Widget>
  );
};

export default StandbyTimeAssignmentsWidget;
